/**
 * Hand-rolled Tailwind bars, matching the idiom already used in Dashboard and Analytics.
 * db-admin has no charting library and this page does not justify adding one.
 */
import type { StatusBreakdown, TrendBucket } from '../utils/hipaaStats';

/** Segment order runs most-complete to least, so progress fills from the left. */
const SEGMENTS: Array<{ key: keyof StatusBreakdown; className: string; label: string }> = [
  { key: 'done', className: 'bg-green-500', label: 'Done' },
  { key: 'partial', className: 'bg-amber-400', label: 'Partial' },
  { key: 'in_progress', className: 'bg-blue-400', label: 'In progress' },
  { key: 'not_started', className: 'bg-gray-200', label: 'Not started' },
];

export const StackedProgressBar = ({
  breakdown,
  height = 'h-2',
}: {
  breakdown: StatusBreakdown;
  height?: string;
}) => {
  // N/A is excluded from the denominator: it is not work, so it should not dilute progress.
  const total = breakdown.total - breakdown.na;
  if (total <= 0) {
    return <div className={`w-full bg-gray-100 rounded-full ${height}`} />;
  }

  return (
    <div className={`w-full flex overflow-hidden rounded-full bg-gray-100 ${height}`}>
      {SEGMENTS.map((segment) => {
        const count = breakdown[segment.key];
        if (count === 0) return null;
        return (
          <div
            key={segment.key}
            className={`${segment.className} transition-all duration-300`}
            style={{ width: `${(count / total) * 100}%` }}
            title={`${segment.label}: ${count}`}
          />
        );
      })}
    </div>
  );
};

export const ProgressLegend = () => (
  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
    {SEGMENTS.map((segment) => (
      <span key={segment.key} className="flex items-center gap-1.5">
        <span className={`w-2.5 h-2.5 rounded-sm ${segment.className}`} />
        {segment.label}
      </span>
    ))}
  </div>
);

/**
 * Weekly completions. Stays flat until people start working in the tracker -- that is
 * expected, not a bug, and the caption says so.
 */
export const CompletionTrend = ({ buckets }: { buckets: TrendBucket[] }) => {
  const peak = Math.max(1, ...buckets.map((bucket) => bucket.completed));
  const total = buckets.reduce((sum, bucket) => sum + bucket.completed, 0);

  return (
    <div>
      <div className="flex items-end gap-1.5 h-24">
        {buckets.map((bucket) => (
          <div key={bucket.weekStart} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex-1 flex items-end">
              <div
                className={`w-full rounded-t transition-all duration-300 ${
                  bucket.completed > 0 ? 'bg-green-500' : 'bg-gray-100'
                }`}
                style={{
                  height: bucket.completed > 0 ? `${(bucket.completed / peak) * 100}%` : '2px',
                }}
                title={`${bucket.completed} completed`}
              />
            </div>
            <span className="text-[10px] text-gray-400 whitespace-nowrap">{bucket.label}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-400">
        {total === 0
          ? 'No items completed yet — this fills in as work lands.'
          : `${total} item${total === 1 ? '' : 's'} completed in the last ${buckets.length} weeks.`}
      </p>
    </div>
  );
};
